import React, { useState, useEffect, useContext, useRef, useCallback, createContext } from "react";
import { apiConfigReady, getApiBaseUrl_dynamic, getSocketUrl_dynamic } from '../config/config.js';
import departures_data from '../data/departure.js';
import city_data from "../data/data.js";
import { io } from 'socket.io-client';
import { recordFlightDataReceipt, updateFlightsDisplayedCount, updateSocketStatus } from '../services/flightDataMonitor.js';
import { serverNow } from '../services/serverClock.js';

// Shared helper functions for flight key generation and deduplication
const serviceDateLocalISO = (iso, tz = 'Africa/Lagos') => {
  if (!iso) return '';
  const d = new Date(iso);
  // Check if date is valid before formatting
  if (isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year:'numeric', month:'2-digit', day:'2-digit' })
    .formatToParts(d)
    .reduce((o,p)=> (o[p.type]=p.value, o), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

// Helper to combine separate date and time fields into ISO datetime
const combineDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  // Combine date (YYYY-MM-DD) and time (HH:MM:SS) into ISO format
  const combined = `${dateStr}T${timeStr}Z`;
  const d = new Date(combined);
  return isNaN(d.getTime()) ? null : combined;
};

const parseCarrierAndNumber = (flightObj) => {
  const code = flightObj.airlineCode || '';
  const num  = flightObj.flightNumber || '';
  if (code && num) return { code, num };
  // fallback: parse combined "AT555"
  const m = String(flightObj.flight || '').match(/^([A-Z0-9]{2,3})\s*0*([0-9]+)/i);
  return m ? { code: m[1].toUpperCase(), num: m[2] } : { code: code || 'XX', num: num || '0000' };
};

const makeFlightKey = (flightObj, { airport='LOS', dir='D', tz='Africa/Lagos' } = {}) => {
  const { code, num } = parseCarrierAndNumber(flightObj);
  const sDate = serviceDateLocalISO(flightObj.scheduledTime || flightObj.estimatedTime, tz);
  return `${airport}:${dir}:${code}${num}:${sDate}`;
};

const byTimeAsc = (a, b) => {
  const ta = new Date(a.estimatedTime || a.scheduledTime || 0).getTime();
  const tb = new Date(b.estimatedTime || b.scheduledTime || 0).getTime();
  return ta - tb; // Ascending: oldest first
};

const upsertByKey = (list, incoming) => {
  if (!incoming?.key) return list;
  const idx = list.findIndex(f => f.key === incoming.key);
  if (idx === -1) return [...list, incoming].sort(byTimeAsc);

  // Optional freshness guard if backend sends a version:
  const old = list[idx];
  const newer = (incoming.version ?? 0) >= (old.version ?? 0) ? incoming : old;

  // Preserve manual override fields: prefer newer, fall back to old
  const next = [...list];
  next[idx] = {
    ...newer,
    arrivalBaggageOverride: newer.arrivalBaggageOverride ?? old.arrivalBaggageOverride ?? null,
    departureGateOverride: newer.departureGateOverride ?? old.departureGateOverride ?? null,
    statusOverride: newer.statusOverride ?? old.statusOverride ?? null,
  };
  return next.sort(byTimeAsc);
};

const uniqueByKey = (list) => {
  const seen = new Map();
  for (const f of list) {
    if (!f?.key) continue;
    const prev = seen.get(f.key);
    // prefer the one with newer version/updatedAt if you have it
    if (!prev || (f.version ?? 0) >= (prev.version ?? 0)) seen.set(f.key, f);
  }
  return Array.from(seen.values()).sort(byTimeAsc);
};

// Filter out stale flights - keep flights within time window
// IMPORTANT: Must match backend window (4h past + 7h future) for consistent reconciliation
const filterStaleFlights = (list, hoursBeforeNow = 4, hoursAfterNow = 7) => {
  const now = serverNow(); // server clock: kiosk device clocks drift
  const pastThreshold = now - (hoursBeforeNow * 60 * 60 * 1000);
  const futureThreshold = now + (hoursAfterNow * 60 * 60 * 1000);

  return list.filter(flight => {
    const flightTime = new Date(flight.actualTime || flight.estimatedTime || flight.scheduledTime || 0).getTime();
    // Keep flights within the time window
    return flightTime >= pastThreshold && flightTime <= futureThreshold;
  });
};

// Reconciliation constants
const RECONCILIATION_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes default
const RECONCILIATION_JITTER_MS = 60 * 1000; // ±1 minute jitter to prevent thundering herd
const DEGRADED_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes when socket is degraded
const FAILURE_BACKOFF_INTERVAL_MS = 60 * 1000; // 1 minute after consecutive failures
const MAX_CONSECUTIVE_FAILURES = 3; // Trigger faster reconciliation after this many failures
const SOCKET_QUIET_THRESHOLD_MS = 30 * 1000; // Skip reconciliation if socket active within this window

const DepartureContext = createContext();
const filter_str = [
    "British Airways",//ok
    "Air France",//OK
    "Alitalia",//
    "SWISS",//ok
    "South Africa Airways",//
    "Ghana Airways",//
    "Ethiopian Airlines",//ok
    "American Airlines",//ok
    "Delta Air lines",//ok
    "KLM",//ok
    "Royal Air Maroc",//ok
    "Air India",//
    "Air Cote Divoire",//
    "EMIRATES",//
    "Singapore Airlines",//
    "Qatar Airways",//ok
    "Turkish Airlines",//ok
    "Air Peace",
    "Lufthansa"//ok
];

const DepartureProvider = ({ children }) => {
    const [idata, setidata] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [lastFlightUpdate, setLastFlightUpdate] = useState(null); // Track when we last received flight data
    const [lastReconciliation, setLastReconciliation] = useState(null);
    const [socketTransport, setSocketTransport] = useState('unknown'); // Track websocket vs polling
    const [lastETag, setLastETag] = useState(null); // For 304 caching

    // Ref to hold socket instance for reconnect capability
    const socketRef = useRef(null);
    const subscribedRef = useRef(false); // guard against duplicate listeners

    // Re-enabled: FLIFO API calls restored with periodic reconciliation
    useEffect(() => {
        console.log('FLIFO API enabled: Fetching departure data from FLIFO v2 API');
        let reconciliationTimer = null;
        let isSocketDegraded = false;
        let consecutiveFailures = 0;
        let lastSocketDataTime = 0; // Track when socket last delivered data
        let socket = null;
        let isCleanedUp = false;

        /**
         * Reconcile local state with REST API (authoritative snapshot)
         * - Upsert all records from REST
         * - Remove records no longer in REST response (within time window)
         * - Prefer newer updates using serverUpdatedAt
         */
        const reconcileWithREST = async (isInitial = false) => {
            const startTime = Date.now();

            // Skip reconciliation if socket recently delivered data (unless initial load)
            if (!isInitial && lastSocketDataTime > 0) {
                const timeSinceSocketData = Date.now() - lastSocketDataTime;
                if (timeSinceSocketData < SOCKET_QUIET_THRESHOLD_MS) {
                    console.log(`📊 Reconciliation skipped: socket active ${Math.round(timeSinceSocketData / 1000)}s ago`);
                    return;
                }
            }

            try {
                const headers = {};
                if (lastETag && !isInitial) {
                    headers['If-None-Match'] = lastETag;
                }

                // Use dynamic API URL (resolves after LAN detection)
                const API_BASE_URL = getApiBaseUrl_dynamic();
                const apiUrl = `${API_BASE_URL}/api/flifo/flight_records?direction=D&airport=LOS&pastWindow=4&futureWindow=7&view=full`;
                const response = await fetch(apiUrl, { headers });

                // Handle 304 Not Modified - data hasn't changed
                if (response.status === 304) {
                    console.log('📊 Reconciliation: 304 Not Modified - no changes');
                    setLastReconciliation(new Date());
                    return;
                }

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                // Store ETag for next request
                const newETag = response.headers.get('ETag');
                if (newETag) {
                    setLastETag(newETag);
                }

                const data = await response.json();
                const restFlights = (data.flights || []).filter(f => f.key);

                console.log(`📊 Reconciliation: Received ${restFlights.length} flights from REST`);

                setidata(prev => {
                    // Build map of REST flights by key
                    const restMap = new Map();
                    for (const flight of restFlights) {
                        restMap.set(flight.key, {
                            ...flight,
                            arrivalBaggageOverride: flight.arrivalBaggageOverride ?? null,
                            departureGateOverride: flight.departureGateOverride ?? null,
                            statusOverride: flight.statusOverride ?? null,
                            direction: 'D'
                        });
                    }

                    // Build map of current local flights
                    const localMap = new Map();
                    for (const flight of prev) {
                        if (flight.key) localMap.set(flight.key, flight);
                    }

                    // Merge: REST is authoritative, but prefer newer socket updates
                    const merged = new Map();

                    // First, add all REST flights
                    for (const [key, restFlight] of restMap) {
                        const localFlight = localMap.get(key);

                        if (localFlight) {
                            // Both exist - prefer the one with newer serverUpdatedAt
                            // Defensive fallback: use updatedAt if serverUpdatedAt missing
                            const restTime = new Date(restFlight.serverUpdatedAt || restFlight.updatedAt || 0).getTime();
                            const localTime = new Date(localFlight.serverUpdatedAt || localFlight.updatedAt || 0).getTime();

                            if (localTime > restTime) {
                                // Local (socket) update is newer - keep it, but preserve overrides from REST
                                merged.set(key, {
                                    ...localFlight,
                                    arrivalBaggageOverride: localFlight.arrivalBaggageOverride ?? restFlight.arrivalBaggageOverride ?? null,
                                    departureGateOverride: localFlight.departureGateOverride ?? restFlight.departureGateOverride ?? null,
                                    statusOverride: localFlight.statusOverride ?? restFlight.statusOverride ?? null,
                                });
                            } else {
                                // REST is newer or same - use REST
                                merged.set(key, restFlight);
                            }
                        } else {
                            // Only in REST - add it
                            merged.set(key, restFlight);
                        }
                    }

                    // Remove flights that are no longer in REST response
                    // (they're outside the time window or have been deleted)
                    const removed = [];
                    for (const [key, localFlight] of localMap) {
                        if (!restMap.has(key)) {
                            removed.push(key);
                        }
                    }

                    if (removed.length > 0) {
                        console.log(`📊 Reconciliation: Removing ${removed.length} stale flights:`, removed.slice(0, 5));
                    }

                    const result = Array.from(merged.values()).sort(byTimeAsc);
                    const reconcileTime = Date.now() - startTime;
                    console.log(`📊 Reconciliation complete: ${result.length} flights (${reconcileTime}ms)`);

                    return result;
                });

                const now = new Date();
                setLastUpdated(now);
                setLastFlightUpdate(now);
                setLastReconciliation(now);
                recordFlightDataReceipt(now);

                // Reset failure counter on success
                consecutiveFailures = 0;

            } catch (err) {
                console.error('📊 Reconciliation failed:', err.message);
                // Track consecutive failures for adaptive interval
                consecutiveFailures++;
                console.log(`📊 Consecutive failures: ${consecutiveFailures}`);
                // Don't update state on error - keep existing data
            }
        };

        // Schedule next reconciliation with jitter
        const scheduleReconciliation = () => {
            // Use faster interval after consecutive failures
            let baseInterval;
            if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                baseInterval = FAILURE_BACKOFF_INTERVAL_MS;
            } else if (isSocketDegraded) {
                baseInterval = DEGRADED_INTERVAL_MS;
            } else {
                baseInterval = RECONCILIATION_INTERVAL_MS;
            }

            const jitter = (Math.random() - 0.5) * 2 * RECONCILIATION_JITTER_MS;
            const interval = Math.max(30000, baseInterval + jitter); // Min 30 seconds

            console.log(`📊 Next reconciliation in ${Math.round(interval / 1000)}s (degraded: ${isSocketDegraded}, failures: ${consecutiveFailures})`);

            reconciliationTimer = setTimeout(() => {
                reconcileWithREST(false);
                scheduleReconciliation(); // Schedule next one
            }, interval);
        };

        // Wait for API config detection, then initialize
        const initializeConnections = async () => {
            // Wait for LAN/cloud detection to complete
            await apiConfigReady;
            if (isCleanedUp) return; // Component unmounted during detection

            const API_BASE_URL = getApiBaseUrl_dynamic();
            const SOCKET_URL = getSocketUrl_dynamic();
            console.log('🌐 API config ready:', { API_BASE_URL: API_BASE_URL || '(relative)', SOCKET_URL: SOCKET_URL || '(same origin)' });

            // Initial fetch using reconciliation (will set initial data)
            reconcileWithREST(true);

            // Start periodic reconciliation
            scheduleReconciliation();

            // Set up Socket.io connection for real-time updates
            const socketUrl = SOCKET_URL;
            console.log('🔌 Socket.IO connecting to:', socketUrl || '(same origin)');
            socket = io(socketUrl || window.location.origin, {
                path: '/socket.io',
                transports: ['websocket', 'polling'],
            });
            socketRef.current = socket; // expose for reconnectSocket()

            // Join room for LOS departures
            socket.emit('join', { room: 'airport:LOS:D' });
            console.log('✅ Joined Socket.io room: airport:LOS:D for real-time updates');

            // Handle reconnections - always reconcile on reconnect for reliability
            socket.on('connect', () => {
                updateSocketStatus(true); // Track socket connection for staleness detection
                console.log('🔌 Socket connected/reconnected');
                socket.emit('join', { room: 'airport:LOS:D' });

                // Track transport type for health monitoring
                const transport = socket.io?.engine?.transport?.name || 'unknown';
                setSocketTransport(transport);
                isSocketDegraded = transport === 'polling';
                console.log(`🔌 Socket transport: ${transport} (degraded: ${isSocketDegraded})`);

                // Always reconcile on reconnect to ensure we have latest state
                // This handles: backend restarts, network blips, long disconnects
                console.log('🔄 Socket reconnected, triggering reconciliation for consistency');
                reconcileWithREST(false);
            });

            // Track transport upgrades (polling -> websocket)
            socket.io?.engine?.on('upgrade', (transport) => {
                const transportName = transport?.name || 'websocket';
                setSocketTransport(transportName);
                isSocketDegraded = transportName === 'polling';
                console.log(`🔌 Socket upgraded to: ${transportName}`);
            });

            socket.on('disconnect', (reason) => {
                updateSocketStatus(false); // Track socket disconnection
                setSocketTransport('disconnected');
                console.warn(`⚠️ Socket disconnected: ${reason}`);
            });

            // Listen for flightUpdate events from FlightBoard (complete flight data)
            socket.on('flightUpdate', (data) => {
                console.log('🔄 Received flightUpdate:', data);
                const { flight, key } = data;
                const stableKey = flight?.key || key || makeFlightKey(flight, { airport:'LOS', dir:'D' });

                setidata(prev => {
                    const updated = upsertByKey(prev, { ...flight, key: stableKey });
                    return filterStaleFlights(updated); // Remove stale flights after update
                });
                const now = new Date();
                setLastUpdated(now);
                setLastFlightUpdate(now);
                recordFlightDataReceipt(now); // Track for staleness detection
                lastSocketDataTime = Date.now(); // Track socket activity for reconciliation timing
            });

            // Listen for FLIGHT_UPSERT events from updated FlightBoard (new format)
            socket.on('FLIGHT_UPSERT', (data) => {
                console.log('🔄 Received FLIGHT_UPSERT:', data);
                const { flight, key } = data;

                // Add null check for flight object
                if (!flight) {
                    console.warn('🚨 FLIGHT_UPSERT received with null/undefined flight object:', data);
                    return;
                }

                // CRITICAL: Always use backend's stable key - never regenerate!
                // Backend uses hash-based key generation for consistency across webhooks
                const stableKey = flight.key || key;

                if (!stableKey) {
                    console.error('🚨 FLIGHT_UPSERT missing key - cannot update flight:', flight);
                    return;
                }

                setidata(prev => {
                    const updated = upsertByKey(prev, { ...flight, key: stableKey });
                    console.log(`✅ Upserted flight ${flight.flight} with key: ${stableKey}`);
                    return filterStaleFlights(updated); // Remove stale flights after update
                });
                const now = new Date();
                setLastUpdated(now);
                setLastFlightUpdate(now);
                recordFlightDataReceipt(now); // Track for staleness detection
                lastSocketDataTime = Date.now(); // Track socket activity for reconciliation timing
            });

            // Legacy FLIGHT_DELTA removed - now using FLIGHT_UPSERT consistently
        };

        // Start initialization
        initializeConnections();

        // Cleanup on unmount
        return () => {
            isCleanedUp = true;
            if (reconciliationTimer) {
                clearTimeout(reconciliationTimer);
                console.log('📊 Reconciliation timer cleared');
            }
            if (socket) {
                socket.disconnect();
                console.log('🔌 Socket.io disconnected');
            }
        };

    }, []); // Initial load + real-time setup + reconciliation

    // Track flight count changes for staleness monitoring
    useEffect(() => {
        updateFlightsDisplayedCount(idata.length);
    }, [idata]);

    const refresh = () => {
        setLoading(true);
        setError(null);
        // Manual refresh would go here if needed
        console.log('Manual refresh triggered');
    };

    const updateFlights = (updater) => setidata(prev =>
        typeof updater === 'function' ? updater(prev) : updater
    );

    /**
     * Disconnect and reconnect the socket, re-subscribing listeners exactly once.
     * Called by self-healing recovery ladder (Step 2: RESUBSCRIBE_SOCKET).
     */
    const reconnectSocket = useCallback(() => {
        const s = socketRef.current;
        if (!s) {
            console.warn('[DepartureCtx] reconnectSocket: no socket instance');
            return;
        }
        console.log('🔌 [DepartureCtx] reconnectSocket: disconnecting…');
        s.disconnect();

        // Short delay to let the disconnect settle, then reconnect
        setTimeout(() => {
            console.log('🔌 [DepartureCtx] reconnectSocket: reconnecting…');
            s.connect();
            // Socket 'connect' handler already re-joins room and triggers reconciliation
        }, 500);
    }, []);

    // Health info for debugging/monitoring
    const healthInfo = {
        lastReconciliation,
        socketTransport,
        flightCount: idata.length
    };

    const value = { idata, loading, error, lastUpdated, lastFlightUpdate, refresh, updateFlights, reconnectSocket, healthInfo };

    return (
        <DepartureContext.Provider value={value}>
            {children}
        </DepartureContext.Provider>
    );
};

const useDeparture = () => {
    const context = useContext(DepartureContext);
    if (!context) {
        throw new Error("useDeparture must be used within a DepartureProvider");
    }
    // Return state + real updater + reconnectSocket
    return [context.idata, context.updateFlights, context.reconnectSocket];
};

export { DepartureProvider, useDeparture };